import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
interface AnalyzingScreenProps {
  onComplete: () => void;
}
const STEPS = [{
  label: 'Procesando tus respuestas',
  delay: 0
}, {
  label: 'Detectando tu perfil emprendedor',
  delay: 600
}, {
  label: 'Calculando tu potencial de ventas',
  delay: 1400
}, {
  label: 'Preparando tu diagnóstico personalizado',
  delay: 2200
}];
const TOTAL_DURATION = 3000;
export function AnalyzingScreen({
  onComplete
}: AnalyzingScreenProps) {
  const [progress, setProgress] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [activeStep, setActiveStep] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(100, elapsed / TOTAL_DURATION * 100);
      setProgress(pct);
      if (pct >= 100) clearInterval(interval);
    }, 30);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    STEPS.forEach((step, idx) => {
      timers.push(setTimeout(() => setActiveStep(idx), step.delay));
      timers.push(setTimeout(() => setCompletedSteps(prev => [...prev, idx]), step.delay + 550));
    });
    timers.push(setTimeout(() => onComplete(), TOTAL_DURATION + 300));
    return () => timers.forEach(clearTimeout);
  }, [onComplete]);
  return <div className="flex flex-col min-h-screen bg-gradient-to-b from-rose-50 via-pink-50 to-amber-50 px-6 py-10 max-w-md mx-auto w-full relative overflow-hidden" data-id="element-10">
      {/* Ambient glows */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-72 h-72 bg-rose-300/40 blur-3xl rounded-full pointer-events-none" data-id="element-11" />
      <div className="absolute bottom-1/4 right-1/4 w-56 h-56 bg-amber-300/30 blur-3xl rounded-full pointer-events-none" data-id="element-12" />

      <div className="relative z-10 flex flex-col items-center justify-center flex-grow text-center" data-id="element-13">
        {/* Spinning donut */}
        <motion.div initial={{
        scale: 0.5,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} className="relative mb-8" data-id="element-14">
          <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: 'linear'
        }} className="w-28 h-28 rounded-full border-[3px] border-rose-200 border-t-rose-500 border-r-rose-500" data-id="element-15" />
          <motion.div animate={{
          rotate: -360
        }} transition={{
          duration: 3.5,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute inset-3 rounded-full border-[2px] border-amber-200 border-b-amber-500 border-l-amber-500" data-id="element-16" />
          <motion.div animate={{
          scale: [1, 1.15, 1],
          rotate: [0, 10, -10, 0]
        }} transition={{
          duration: 1.6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute inset-0 flex items-center justify-center text-5xl drop-shadow-lg" data-id="element-17">
            🍩
          </motion.div>
        </motion.div>

        <motion.h2 initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.2
      }} className="font-display font-black text-rose-950 text-[28px] leading-tight mb-2 tracking-tight" data-id="element-18">
          ⏳ Analizando tus respuestas...
        </motion.h2>
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.4,
        delay: 0.35
      }} className="text-sm text-rose-900/75 font-medium mb-8 max-w-xs" data-id="element-19">
          Estamos preparando tu diagnóstico personalizado...
        </motion.p>

        {/* Progress bar */}
        <div className="w-full max-w-xs mb-8" data-id="element-20">
          <div className="h-2.5 w-full bg-rose-100 rounded-full overflow-hidden shadow-inner" data-id="element-21">
            <motion.div className="h-full bg-gradient-to-r from-rose-500 via-pink-500 to-amber-400 rounded-full relative" style={{
            width: `${progress}%`
          }} data-id="element-22">
              <div className="absolute inset-0 bg-white/25 animate-pulse" data-id="element-23" />
            </motion.div>
          </div>
          <p className="text-[12px] font-extrabold text-rose-700 mt-2 tabular-nums" data-id="element-24">
            {Math.round(progress)}%
          </p>
        </div>

        {/* Checklist */}
        <div className="w-full max-w-xs flex flex-col gap-2.5 text-left" data-id="element-25">
          {STEPS.map((step, idx) => {
          const isCompleted = completedSteps.includes(idx);
          const isActive = activeStep === idx && !isCompleted;
          return <AnimatePresence key={idx} data-id="element-26">
                {(isActive || isCompleted) && <motion.div initial={{
              opacity: 0,
              x: -15
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.35
            }} className="flex items-center gap-3" data-id="element-27">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors duration-300 ${isCompleted ? 'bg-emerald-500 text-white' : 'bg-rose-100 border-2 border-rose-500'}`} data-id="element-28">
                      {isCompleted ? <motion.svg initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 15
                }} className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-29">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-30" />
                        </motion.svg> : <motion.div animate={{
                  rotate: 360
                }} transition={{
                  duration: 0.8,
                  repeat: Infinity,
                  ease: 'linear'
                }} className="w-3 h-3 rounded-full border-2 border-rose-500 border-t-transparent" data-id="element-31" />}
                    </div>
                    <span className={`text-sm font-semibold transition-colors duration-300 ${isCompleted ? 'text-rose-900/55' : 'text-rose-950'}`} data-id="element-32">
                      {step.label}
                    </span>
                  </motion.div>}
              </AnimatePresence>;
        })}
        </div>
      </div>
    </div>;
}